import { useEffect, useRef, useState } from "react";

const CHARS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ!@#$%&*1234567890";

/**
 * ScrambleText — on mount / on trigger, cycles through random chars
 * until revealing the target string letter-by-letter.
 *
 * Props:
 *   text: string
 *   trigger: "mount" | "hover"
 *   duration: ms
 */
export default function ScrambleText({
  text,
  trigger = "hover",
  duration = 900,
  className = "",
}) {
  const [display, setDisplay] = useState(text);
  const running = useRef(false);
  const rafId = useRef(null);

  const run = () => {
    if (running.current) return;
    running.current = true;
    const start = performance.now();
    const target = text;
    const tick = (now) => {
      const p = Math.min(1, (now - start) / duration);
      const resolveUpTo = Math.floor(p * target.length);
      let out = "";
      for (let i = 0; i < target.length; i++) {
        if (i < resolveUpTo || target[i] === " ") {
          out += target[i];
        } else {
          out += CHARS[Math.floor(Math.random() * CHARS.length)];
        }
      }
      setDisplay(out);
      if (p < 1) {
        rafId.current = requestAnimationFrame(tick);
      } else {
        setDisplay(target);
        running.current = false;
      }
    };
    rafId.current = requestAnimationFrame(tick);
  };

  useEffect(() => {
    if (trigger === "mount") run();
    return () => {
      if (rafId.current) cancelAnimationFrame(rafId.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlers =
    trigger === "hover"
      ? { onMouseEnter: run, onFocus: run }
      : {};

  return (
    <span className={className} {...handlers}>
      {display}
    </span>
  );
}
